import type { ReactNode } from 'react';

import { Trans } from '@lingui/react/macro';
import { WarningTriangle } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';

import { IntegrationException } from 'src/api/endpoints/cloudApi.types';
import { Button, Modal } from 'src/components';

import { DecodeProviderCallbackException } from '../utils/decodeErrors';

const getErrorContent = (
  error: DecodeProviderCallbackException | Error
): { title: ReactNode; description?: ReactNode } => {
  if (
    error instanceof DecodeProviderCallbackException &&
    error.type === 'ACCESS_DENIED'
  ) {
    return {
      title: (
        <Trans context="save-integration-modal">
          Unable to access your account
        </Trans>
      ),
      description: (
        <Trans context="save-integration-modal">
          It looks like there are some missing permissions that are needed for
          this feature to work.
        </Trans>
      ),
    };
  }

  if (error instanceof IntegrationException && error.type === 'ALREADY_ADDED') {
    return {
      title: (
        <Trans context="save-integration-modal">
          Sorry, it looks like this account has already been added
        </Trans>
      ),
    };
  }

  if (
    error instanceof IntegrationException &&
    error.type === 'ACCOUNT_MISMATCH'
  ) {
    return {
      title: (
        <Trans context="save-integration-modal">
          Account does not match the existing integration
        </Trans>
      ),
      description: (
        <Trans context="save-integration-modal">
          The email address for the integration that you’re trying to
          reauthorize is different from what you have entered previously.
        </Trans>
      ),
    };
  }

  return {
    title: (
      <Trans context="save-integration-modal">
        Sorry, something went wrong
      </Trans>
    ),
    description: (
      <Trans context="save-integration-modal">
        We’re currently trying to fix this issue. Please try again later.
      </Trans>
    ),
  };
};

export const SaveIntegrationError = ({
  error,
}: {
  error: DecodeProviderCallbackException | Error;
}) => {
  const { title, description } = getErrorContent(error);

  return (
    <div
      className="flex flex-col items-center gap-32"
      data-cy="save-integration-error"
    >
      <span className="flex items-center justify-center rounded-full bg-red-light-4 p-8">
        <WarningTriangle size={32} />
      </span>

      <Modal.Title asChild>
        <Typography as="h2" variant="heading-md">
          {title}
        </Typography>
      </Modal.Title>
      {description && (
        <Modal.Description asChild>
          <Typography variant="body-lg">{description}</Typography>
        </Modal.Description>
      )}
      <Modal.Close asChild>
        <Button variant="primary" data-cy="save-integration-error-close">
          <Trans context="save-integration-modal">Close</Trans>
        </Button>
      </Modal.Close>
    </div>
  );
};
